import { z } from "zod";
import { articleSummarySchema } from "@/features/articles";
import { pageImageLinkSchema } from "@/features/salon";

/**
 * What the salon wrote for its landing page, as `GET /salon/public/home` returns it (design note
 * salon-home-landing.md section 7b; TDD-SA FR-SA-191). Every section and field is optional: what is
 * missing falls back to the salon's real data. The server checked the limits on the way in; here
 * the shapes are checked again on the way out, and text is only ever rendered as text.
 */
const text = z.string().nullable().default(null);
const money = z.string().regex(/^\d{1,7}\.\d{2}$/);

export const publicImageSchema = z.object({ url: pageImageLinkSchema, altText: z.string().default("") });

export const WHY_US_ICONS = [
  "scissors", "award", "sparkles", "leaf", "heart", "star", "gem", "crown",
  "droplets", "flower", "smile", "clock", "shield-check", "hand-heart", "palette", "brush",
] as const;

export const publicHomePageSchema = z.object({
  hero: z
    .object({
      headline: text,
      highlight: text,
      text,
      buttonLabel: text,
      image: publicImageSchema.nullable().catch(null).default(null),
      /** The arch's pictures in order (FR-SA-205). */
      images: z.array(publicImageSchema).catch([]).default([]),
      /** The round picture the salon chose for the foot of the arch (FR-SA-258); null, the page picks one. */
      inset: publicImageSchema.nullable().catch(null).default(null),
    })
    // An API from before the list sends only `image`: read it as a list of one.
    .transform((hero) => ({ ...hero, images: hero.images.length > 0 ? hero.images : hero.image ? [hero.image] : [] }))
    .optional(),
  about: z
    .object({
      visible: z.boolean().default(true),
      heading: text,
      text,
      stats: z.array(z.object({ value: z.string(), label: z.string() })).max(4).default([]),
    })
    .optional(),
  services: z
    .object({
      visible: z.boolean().default(true),
      heading: text,
      categoryImages: z.record(z.string(), publicImageSchema).catch({}).default({}),
      categoryTexts: z.record(z.string(), z.string()).default({}),
    })
    .optional(),
  /** The featured treatment (TDD-SA FR-SA-203): blanks fall back to the service's own; no service, no band. */
  featured: z
    .object({
      visible: z.boolean().default(true),
      serviceId: z.string().nullable().default(null),
      eyebrow: text,
      title: text,
      highlight: text,
      text,
      image: publicImageSchema.nullable().catch(null).default(null),
    })
    .optional(),
  whyUs: z
    .object({
      visible: z.boolean().default(true),
      heading: text,
      items: z
        .array(z.object({ icon: z.enum(WHY_US_ICONS).catch("sparkles"), title: z.string(), text }))
        .max(3)
        .default([]),
    })
    .optional(),
  gallery: z
    .object({
      visible: z.boolean().default(true),
      heading: text,
      text,
      images: z.array(publicImageSchema).max(12).catch([]).default([]),
    })
    .optional(),
  prices: z
    .object({
      visible: z.boolean().default(true),
      heading: text,
      mode: z.enum(["menu", "plans"]).catch("menu").default("menu"),
      plans: z
        .array(
          z.object({
            name: z.string(),
            price: money,
            period: z.enum(["month", "visit"]).catch("visit"),
            highlighted: z.boolean().default(false),
            lines: z.array(z.object({ label: z.string(), price: money.nullable().default(null) })).max(5).default([]),
          }),
        )
        .max(6)
        .default([]),
    })
    .optional(),
  team: z.object({ visible: z.boolean().default(true), heading: text }).optional(),
  /** Written by the salon, never taken from feedback (design note 9.1; TDD-SA FR-SA-194, FR-SA-100). */
  testimonials: z
    .object({
      visible: z.boolean().default(true),
      heading: text,
      items: z
        .array(
          z.object({
            quote: z.string(),
            name: z.string(),
            role: text,
            rating: z.number().int().min(1).max(5).catch(5),
            image: publicImageSchema.nullable().catch(null).default(null),
          }),
        )
        .max(12)
        .default([]),
    })
    .optional(),
  /** "From the chair" on home (TDD-SA FR-SA-192). */
  articles: z.object({ visible: z.boolean().default(true), heading: text, text }).optional(),
  /** The three newest published articles, for the home section. */
  latestArticles: z.array(articleSummarySchema).max(3).catch([]).default([]),
  /** How many pictures and videos the gallery holds, for "View all" and the menu (FR-SA-195). */
  galleryItemCount: z.number().int().min(0).catch(0).default(0),
});

export type PublicHomePage = z.infer<typeof publicHomePageSchema>;
export type PublicImage = z.infer<typeof publicImageSchema>;
export type WhyUsIcon = (typeof WHY_US_ICONS)[number];
